import {
    AlignmentType,
    BorderStyle,
    Document,
    Footer,
    Header,
    ImageRun,
    LevelFormat,
    Packer,
    Paragraph,
    Table,
    TableCell,
    TableRow,
    TextRun,
    VerticalAlign,
    WidthType,
} from 'docx';
import { IntlShape } from 'react-intl';
import { ActivityNimReadDTO, ListOfValuesReadDTO, ParticipantRoleCode, WorkingTimeCode } from '../../api/dto';
import cernLogoUrl from '../../assets/cern-logo.png';

export interface GuidedVisitDocData {
    activity: ActivityNimReadDTO;
    generatedBy: string;
    intl: IntlShape;
}

const FONT = 'Times New Roman';
const SIZE_BODY = 22; // 11pt (half-points)
const SIZE_SMALL = 18; // 10pt
const MIN_SIGNATURE_ROWS = 18;

const formatBirthDate = (d?: string | null): string => {
    if (!d || !/^\d{8}$/.test(d)) return d ?? '';
    return `${d.slice(6, 8)}-${d.slice(4, 6)}-${d.slice(0, 4)}`; // 19850712 → 12-07-1985
};

const text = (t: string, opts: { bold?: boolean; italics?: boolean; underline?: boolean; size?: number } = {}) =>
    new TextRun({
        text: t,
        font: FONT,
        size: opts.size ?? SIZE_BODY,
        bold: opts.bold,
        italics: opts.italics,
        underline: opts.underline ? { type: 'single' } : undefined,
    });

const para = (
    t: string,
    opts: { bold?: boolean; italics?: boolean; underline?: boolean; size?: number; after?: number } = {}
) => new Paragraph({ children: [text(t, opts)], spacing: { after: opts.after ?? 60 } });

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' } as const;

const cell = (
    content: string,
    width: number,
    opts: {
        bold?: boolean;
        borderless?: boolean;
        align?: (typeof AlignmentType)[keyof typeof AlignmentType];
        size?: number;
    } = {}
) =>
    new TableCell({
        width: { size: width, type: WidthType.DXA },
        verticalAlign: VerticalAlign.CENTER,
        borders: opts.borderless ? { top: NO_BORDER, bottom: NO_BORDER, left: NO_BORDER, right: NO_BORDER } : undefined,
        children: [
            new Paragraph({
                alignment: opts.align,
                children: [text(content, { bold: opts.bold, size: opts.size ?? SIZE_BODY })],
            }),
        ],
    });

const twoColRow = (label: string, value: string, widths: [number, number]) =>
    new TableRow({
        children: [cell(label, widths[0], { size: SIZE_BODY }), cell(value, widths[1], { size: SIZE_SMALL })],
    });

export const generateGuidedVisitDocx = async ({ activity, generatedBy, intl }: GuidedVisitDocData): Promise<Blob> => {
    const year = String(new Date().getFullYear());
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const generatedOn = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()} ${pad(
        now.getHours()
    )}:${pad(now.getMinutes())}`;

    const logoRes = await fetch(cernLogoUrl);
    if (!logoRes.ok) throw new Error(`Failed to load CERN logo: ${logoRes.status}`);
    const logo = await logoRes.arrayBuffer();

    const header = new Header({
        children: [
            new Table({
                columnWidths: [949, 7806, 1092],
                width: { size: 9847, type: WidthType.DXA },
                alignment: AlignmentType.CENTER,
                rows: [
                    new TableRow({
                        children: [
                            new TableCell({
                                width: { size: 949, type: WidthType.DXA },
                                verticalAlign: VerticalAlign.CENTER,
                                borders: { top: NO_BORDER, bottom: NO_BORDER, left: NO_BORDER, right: NO_BORDER },
                                children: [
                                    new Paragraph({
                                        children: [
                                            new ImageRun({
                                                type: 'png',
                                                data: logo,
                                                transformation: { width: 46, height: 46 },
                                            }),
                                        ],
                                    }),
                                ],
                            }),
                            new TableCell({
                                width: { size: 7806, type: WidthType.DXA },
                                verticalAlign: VerticalAlign.CENTER,
                                borders: { top: NO_BORDER, bottom: NO_BORDER, left: NO_BORDER, right: NO_BORDER },
                                children: [
                                    new Paragraph({
                                        alignment: AlignmentType.CENTER,
                                        children: [
                                            text(
                                                intl.formatMessage(
                                                    {
                                                        id: 'guidedVisit.doc.headerTitle',
                                                    },
                                                    {
                                                        facility: activity.facility
                                                            ? intl.locale === 'fr'
                                                                ? activity.facility.labelFr
                                                                : activity.facility.labelEn
                                                            : '',
                                                    }
                                                ),
                                                {
                                                    bold: true,
                                                    size: SIZE_SMALL,
                                                }
                                            ),
                                        ],
                                    }),
                                ],
                            }),
                            cell(year, 1092, { borderless: true, align: AlignmentType.RIGHT, size: SIZE_SMALL }),
                        ],
                    }),
                ],
            }),
        ],
    });

    const footer = new Footer({
        children: [
            new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [text(year, { size: SIZE_BODY })],
            }),
        ],
    });

    // ---- Info table ----
    const INFO_WIDTHS: [number, number] = [2694, 6945];
    const date = activity.scheduledStart ?? activity.proposedDate;
    const formatWorkingTime = (
        wt: ListOfValuesReadDTO | null | undefined,
        start: string | null | undefined,
        end: string | null | undefined
    ): string | null => {
        if (wt == null) return null;
        if (wt.code === WorkingTimeCode.OTHER) {
            if (start != null && end != null) return `${start} - ${end}`;
        }
        return intl.locale === 'fr' ? wt.labelFr : wt.labelEn;
    };

    const formatPhone = (phone3Display: string | null | undefined, phone1: string | null | undefined) => {
        if (phone3Display && phone1) return `${phone3Display}, Contact Phone: ${phone1}`;
        return phone1 || phone3Display || '';
    };
    const getTime = () =>
        formatWorkingTime(activity.workingTimeGtd, activity.workingTimeGrtdStart, activity.workingTimeGrtdEnd) ??
        formatWorkingTime(activity.workingTimeAvail, activity.workingTimeAvailStart, activity.workingTimeAvailEnd) ??
        '';
    const infoTable = new Table({
        columnWidths: INFO_WIDTHS,
        width: { size: 9639, type: WidthType.DXA },
        alignment: AlignmentType.CENTER,
        rows: [
            twoColRow(
                intl.formatMessage({
                    id: 'guidedVisit.doc.impact',
                }),
                String(activity.id),
                INFO_WIDTHS
            ),
            twoColRow(
                intl.formatMessage({
                    id: 'guidedVisit.doc.dateAndTime',
                }),
                date
                    ? `${intl.formatDate(new Date(date), {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                      })} ${getTime()}`
                    : '',
                INFO_WIDTHS
            ),
            twoColRow(
                intl.formatMessage({
                    id: 'guidedVisit.doc.visitName',
                }),
                activity.title,
                INFO_WIDTHS
            ),
            twoColRow(
                intl.formatMessage({
                    id: 'guidedVisit.doc.undergroundAreaVisited',
                }),
                activity.activityLocations.map((a) => a.location.impactName).join(', '),
                INFO_WIDTHS
            ),
            twoColRow(
                intl.formatMessage({
                    id: 'guidedVisit.doc.guideName',
                }),
                activity.activityParticipants
                    .filter(
                        (a) =>
                            (a.role.code === ParticipantRoleCode.SUPERVISOR ||
                                a.role.code === ParticipantRoleCode.ESCORT) &&
                            a.person != null
                    )
                    .map((a) =>
                        intl.formatMessage(
                            {
                                id: 'guidedVisit.doc.guideEntry',
                            },
                            {
                                name: a.person?.searchLabel ?? '',
                                id: a.person?.id ?? '',
                                orgUnit: a.person?.orgUnit?.code ?? '',
                                phone: formatPhone(a.person?.phone3Display, a.person?.phone1),
                            }
                        )
                    )
                    .join(', '),
                INFO_WIDTHS
            ),
        ],
    });

    // ---- Dosimeter table ----
    const DOSI_WIDTHS: [number, number] = [3174, 3282];
    const dosimeterTable = new Table({
        columnWidths: DOSI_WIDTHS,
        width: { size: 6456, type: WidthType.DXA },
        alignment: AlignmentType.CENTER,
        rows: [
            new TableRow({
                children: [
                    cell(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.dosimeter',
                        }),
                        DOSI_WIDTHS[0]
                    ),
                    cell('', DOSI_WIDTHS[1]),
                ],
            }),
            twoColRow(
                intl.formatMessage({
                    id: 'guidedVisit.doc.doseAtStart',
                }),
                '',
                DOSI_WIDTHS
            ),
            twoColRow(
                intl.formatMessage({
                    id: 'guidedVisit.doc.doseAtEnd',
                }),
                '',
                DOSI_WIDTHS
            ),
        ],
    });

    // ---- Signature table: header + participants rows ----
    const SIG_WIDTHS = [500, 2282, 2282, 2282, 2282];
    const sigHeader = new TableRow({
        children: [
            cell('', SIG_WIDTHS[0], { bold: true }),
            cell(
                intl.formatMessage({
                    id: 'guidedVisit.doc.name',
                }),
                SIG_WIDTHS[1],
                { bold: true }
            ),
            cell(
                intl.formatMessage({
                    id: 'guidedVisit.doc.firstName',
                }),
                SIG_WIDTHS[2],
                { bold: true }
            ),
            cell(
                intl.formatMessage({
                    id: 'guidedVisit.doc.dateOfBirth',
                }),
                SIG_WIDTHS[3],
                { bold: true }
            ),
            cell(
                intl.formatMessage({
                    id: 'guidedVisit.doc.signature',
                }),
                SIG_WIDTHS[4],
                { bold: true }
            ),
        ],
    });

    const visitors = activity.activityParticipants.filter(
        (a) => a.role.code === ParticipantRoleCode.EXECUTANT && (a.person != null || a.unknown != null)
    );
    const sigRows = Array.from({ length: Math.max(MIN_SIGNATURE_ROWS, visitors.length) }, (_, i) => {
        if (visitors[i]?.unknown != null) {
            return new TableRow({
                children: [
                    cell(String(i + 1), SIG_WIDTHS[0], { size: SIZE_SMALL }),
                    cell(visitors[i]?.unknown ?? '', SIG_WIDTHS[1], { size: SIZE_SMALL }),
                    cell('', SIG_WIDTHS[2], { size: SIZE_SMALL }),
                    cell('', SIG_WIDTHS[3], { size: SIZE_SMALL }),
                    cell('', SIG_WIDTHS[4], { size: SIZE_SMALL }),
                ],
            });
        }
        const person = visitors[i]?.person;
        return new TableRow({
            children: [
                cell(String(i + 1), SIG_WIDTHS[0], { size: SIZE_SMALL }),
                cell(person?.lastName ?? '', SIG_WIDTHS[1], { size: SIZE_SMALL }),
                cell(person?.firstName ?? '', SIG_WIDTHS[2], { size: SIZE_SMALL }),
                cell(formatBirthDate(person?.birthDate), SIG_WIDTHS[3], { size: SIZE_SMALL }),
                cell('', SIG_WIDTHS[4], { size: SIZE_SMALL }),
            ],
        });
    });
    const signatureTable = new Table({
        columnWidths: SIG_WIDTHS,
        width: { size: 9628, type: WidthType.DXA },
        alignment: AlignmentType.CENTER,
        rows: [sigHeader, ...sigRows],
    });

    const bullet = (t: string) =>
        new Paragraph({
            children: [text(t)],
            numbering: { reference: 'visit-bullets', level: 0 },
            spacing: { after: 60 },
        });

    const doc = new Document({
        numbering: {
            config: [
                {
                    reference: 'visit-bullets',
                    levels: [
                        {
                            level: 0,
                            format: LevelFormat.BULLET,
                            text: '\u2022',
                            alignment: AlignmentType.LEFT,
                            style: { paragraph: { indent: { left: 720, hanging: 360 } } },
                        },
                    ],
                },
            ],
        },
        sections: [
            {
                properties: {
                    page: {
                        size: { width: 11899, height: 16840 }, // A4
                        margin: { top: 993, right: 1134, bottom: 1134, left: 1134, header: 709, footer: 739 },
                    },
                },
                headers: { default: header },
                footers: { default: footer },
                children: [
                    infoTable,
                    new Paragraph({ children: [], spacing: { after: 60 } }),
                    dosimeterTable,
                    new Paragraph({ children: [], spacing: { after: 60 } }),
                    para(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.beforeComingIntro',
                        }),
                        {
                            size: SIZE_BODY,
                            italics: true,
                            underline: true,
                        }
                    ),
                    bullet(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.beforeComing1',
                        })
                    ),
                    bullet(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.beforeComing2',
                        })
                    ),
                    new Paragraph({ children: [], spacing: { after: 60 } }),
                    para(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.atVisitPointIntro',
                        }),
                        { size: SIZE_BODY, italics: true, underline: true }
                    ),
                    bullet(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.atVisitPoint1',
                        })
                    ),
                    bullet(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.atVisitPoint2',
                        })
                    ),
                    bullet(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.atVisitPoint3',
                        })
                    ),
                    bullet(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.atVisitPoint4',
                        })
                    ),
                    new Paragraph({ children: [], spacing: { after: 60 } }),
                    para(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.toBeSigned',
                        }),
                        { size: SIZE_BODY, italics: true, underline: true }
                    ),
                    para(
                        intl.formatMessage({
                            id: 'guidedVisit.doc.declaration',
                        }),
                        { size: SIZE_BODY, bold: true }
                    ),
                    new Paragraph({ children: [], spacing: { after: 60 } }),
                    signatureTable,
                    new Paragraph({
                        spacing: { before: 180 },
                        alignment: AlignmentType.CENTER,
                        children: [
                            text(
                                intl.formatMessage(
                                    {
                                        id: 'guidedVisit.doc.generatedBy',
                                    },
                                    { generatedBy, generatedOn }
                                ),
                                {
                                    size: SIZE_SMALL,
                                    italics: true,
                                }
                            ),
                        ],
                    }),
                ],
            },
        ],
    });

    return Packer.toBlob(doc);
};
